import {defineCustomElement} from '../../utils/defineCustomElement.js';
import {MapLoadError} from './mapLoadError.js';
import {t} from '../../localization/translate.js';

class LocalGroupsMap extends HTMLElement {
    connectedCallback() {
        this.controller = new AbortController();
        this.observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                this.observer.disconnect();
                void this.load();
            }
        }, {rootMargin: '100px'});
        this.observer.observe(this);
        this.querySelector('[data-lgm-retry]').addEventListener('click', () => void this.load(), {
            signal: this.controller.signal,
        });
    }

    disconnectedCallback() {
        this.observer.disconnect();
        this.controller.abort();
        this.instance?.destroy();
        this.instance = null;
        this.loading = false;
    }

    /** Load only while this element is still connected. */
    async load() {
        const {signal} = this.controller;
        if (this.loading || this.instance) {
            return;
        }
        this.loading = true;
        const status = this.querySelector('[data-lgm-status]');
        const message = this.querySelector('[data-lgm-status-message]');
        const retry = this.querySelector('[data-lgm-retry]');
        status.hidden = false;
        retry.hidden = true;
        message.textContent = t('Loading map…');
        try {
            const {initLocalGroupsMap} = await import('./index.js');
            signal.throwIfAborted();
            this.instance = await initLocalGroupsMap(this, signal);
            status.hidden = true;
        } catch (error) {
            if (!signal.aborted) {
                message.textContent = t('The map could not load. Please try again.');
                retry.hidden = false;
                if (!(error instanceof MapLoadError)) {
                    window.dispatchEvent(new CustomEvent('log:error', {detail: error}));
                }
            }
        } finally {
            if (this.controller.signal === signal) {
                this.loading = false;
            }
        }
    }
}

defineCustomElement('local-groups-map', LocalGroupsMap);
